/**
 * Pure helpers shared by the two Carbon drift tables (DriftTableWithBulkActions
 * for DSC tenants, HybridDriftManagementTab for Hybrid tenants) and the drift
 * detail (DriftDetailDrawer). A plain module so the component files stay
 * component-only (react-refresh) and the helpers stay unit-testable.
 */

import { format, isToday } from 'date-fns'
import { severityFromApi } from '@/components/carbon/severity'
import type { SeverityLevel } from '@/components/carbon/types'
import { workloadLabelForResourceType } from '@/lib/tcmResourceNames'

/** A drift severity word ("Critical", "High", …) as a Carbon severity level; null when the row has none. */
export function driftSeverityLevel(severity: string | null | undefined): SeverityLevel | null {
  if (!severity) return null
  return severityFromApi(severity)
}

/**
 * The open queue's empty slot before Guardian has assessed the tenant, in the
 * same words in both drift tables. Never the all-clear ("Nothing to review."):
 * unknown is not zero. Says when drift appears, not what to run, because the
 * page offers no scan control.
 */
export const QUEUE_NOT_ASSESSED = {
  title: 'No baseline data yet.',
  body: 'Drifts appear here after the first assessment.',
} as const

/** Per-severity counts for the Severity select, when the whole queue's are known. */
export interface SeverityCounts {
  critical: number
  high: number
  medium: number
  low: number
}

/**
 * The Severity select's options, shared by both drift tables. Unknown is never
 * zero: while nothing is scanned (`countsUnknown`) no option carries a count,
 * not even All. A null `total` (the queue's total is not known) leaves All
 * bare, and missing `severityCounts` leave every severity bare, so the select
 * never counts a loaded page as if it were the queue.
 */
export function severityFilterOptions(
  total: number | null | undefined,
  severityCounts: SeverityCounts | undefined,
  countsUnknown: boolean,
): { value: string; label: string }[] {
  const named = (label: string, count: number | null | undefined) =>
    count == null || countsUnknown ? label : `${label} (${count})`
  return [
    { value: '', label: named('All', total) },
    { value: 'Critical', label: named('Critical', severityCounts?.critical) },
    { value: 'High', label: named('High', severityCounts?.high) },
    { value: 'Medium', label: named('Medium', severityCounts?.medium) },
    { value: 'Low', label: named('Low', severityCounts?.low) },
  ]
}

/** Severity order for sorting and for picking a group's worst member: lower is worse. */
export const SEVERITY_RANK: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 }

/**
 * The "When" column (mockup 02): "Today 14:20", "Tue 14:20" within the last
 * week, "Sep 25" before that. Never a fabricated value: a missing or
 * unparseable timestamp reads "—".
 */
export function formatDriftWhen(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  if (isToday(d)) return `Today ${format(d, 'HH:mm')}`
  const ageMs = now.getTime() - d.getTime()
  if (ageMs >= 0 && ageMs < 6 * 86_400_000) return format(d, 'EEE HH:mm')
  return format(d, 'MMM d')
}

/** A full timestamp for timelines and titles: "Sep 25, 2026 · 14:20". */
export function formatDriftTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return format(d, 'MMM d, yyyy · HH:mm')
}

/** Adds or removes one id in a controlled expansion list (DataTable `expandable.expandedIds`). */
export function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
}

/** True when a setting value is too long or structured for a 240px tag and needs a code block. */
export function isLongValue(value: string | null | undefined): boolean {
  if (!value) return false
  return value.length > 48 || value.includes('\n')
}

export interface AdminCenterLink {
  /** "Revert in SharePoint admin center". */
  label: string
  href: string
}

/**
 * Where a workload's setting is reverted (blueprint §7 B13: the workload's
 * admin-center landing page). Keyed by the customer-facing workload label
 * (`workloadLabelForResourceType`), so DSC components and TCM resource types
 * resolve the same way. Microsoft 365's own landing page is the fallback.
 */
const ADMIN_CENTERS: Record<string, AdminCenterLink> = {
  Entra: { label: 'Revert in Entra admin center', href: 'https://entra.microsoft.com' },
  Exchange: { label: 'Revert in Exchange admin center', href: 'https://admin.exchange.microsoft.com' },
  Intune: { label: 'Revert in Intune admin center', href: 'https://intune.microsoft.com' },
  Teams: { label: 'Revert in Teams admin center', href: 'https://admin.teams.microsoft.com' },
  Defender: { label: 'Revert in the Defender portal', href: 'https://security.microsoft.com' },
  Purview: { label: 'Revert in the Purview portal', href: 'https://purview.microsoft.com' },
  SharePoint: { label: 'Revert in SharePoint admin center', href: 'https://admin.microsoft.com/sharepoint' },
  OneDrive: { label: 'Revert in SharePoint admin center', href: 'https://admin.microsoft.com/sharepoint' },
}

const M365_ADMIN_CENTER: AdminCenterLink = {
  label: 'Revert in Microsoft 365 admin center',
  href: 'https://admin.microsoft.com',
}

export function adminCenterFor(resourceType: string | null | undefined): AdminCenterLink {
  return ADMIN_CENTERS[workloadLabelForResourceType(resourceType)] ?? M365_ADMIN_CENTER
}

/**
 * Walks a paged list until every row is read or `maxPages` is hit. `total` is
 * the server's count, so a caller can tell a complete read (items.length >=
 * total) from a capped one.
 */
export async function collectPages<T>(
  fetchPage: (page: number) => Promise<{ items: T[]; total: number }>,
  maxPages: number,
): Promise<{ items: T[]; total: number }> {
  const items: T[] = []
  let total = 0
  for (let page = 1; page <= maxPages; page++) {
    const res = await fetchPage(page)
    total = res.total
    items.push(...res.items)
    if (items.length >= total || res.items.length === 0) break
  }
  return { items, total }
}
